import { useEffect } from "react";

/* ============================================================
   <CustomScripts />
   Mounted once at the App root. Fetches enabled third-party
   scripts from /api/scripts/public and injects each into the
   DOM at the configured placement:
     • head        → inside <head>
     • body_start  → as the first child of <body>
     • body_end    → as the last child of <body>

   Injected elements are tagged with data-cs-id so we can
   dedupe re-renders and clean up stale scripts on unmount.
============================================================ */
const BASE = process.env.REACT_APP_BACKEND_URL;

/* Parse an arbitrary code blob (which may contain <script>…</script>
   tags, plain JS, noscript pixels, iframes, or mixed HTML) into a
   document fragment we can append to the chosen host. This is the
   only safe way to inject <script> tags via innerHTML — browsers
   ignore them unless they are re-created as fresh Script nodes. */
const buildFragment = (code, ownerId) => {
  const frag = document.createDocumentFragment();
  const wrap = document.createElement("div");
  wrap.innerHTML = code;

  // Walk the parsed children. For <script> we must re-create the
  // element so the browser actually executes it. Everything else
  // (noscript, img pixels, iframe, meta) is cloned as-is.
  Array.from(wrap.childNodes).forEach((node) => {
    if (node.nodeType !== 1) {
      // text / comment — wrap in a span marker so we can track ownership
      if (node.nodeType === 3 && !String(node.nodeValue || "").trim()) return;
      const span = document.createElement("span");
      span.setAttribute("data-cs-id", ownerId);
      span.style.display = "none";
      span.appendChild(node.cloneNode(true));
      frag.appendChild(span);
      return;
    }
    if (node.tagName === "SCRIPT") {
      const s = document.createElement("script");
      // copy every attribute
      Array.from(node.attributes || []).forEach((attr) => {
        s.setAttribute(attr.name, attr.value);
      });
      if (node.textContent) s.text = node.textContent;
      s.setAttribute("data-cs-id", ownerId);
      frag.appendChild(s);
    } else {
      const clone = node.cloneNode(true);
      if (clone.setAttribute) clone.setAttribute("data-cs-id", ownerId);
      frag.appendChild(clone);
    }
  });

  return frag;
};

const mountScript = (script) => {
  if (!script || !script.enabled || !script.code || !script.code.trim()) return;
  const id = script.id;
  // Prevent duplicate injection — React StrictMode, HMR, or accidental
  // re-renders would otherwise stack the same script multiple times.
  if (document.querySelector(`[data-cs-id="${CSS.escape(id)}"]`)) return;

  let host = document.head;
  let position = "append";
  if (script.placement === "body_start") {
    host = document.body;
    position = "prepend";
  } else if (script.placement === "body_end") {
    host = document.body;
    position = "append";
  }
  if (!host) return;

  const frag = buildFragment(script.code, id);
  if (position === "prepend") {
    host.insertBefore(frag, host.firstChild);
  } else {
    host.appendChild(frag);
  }
};

const cleanupScript = (id) => {
  document
    .querySelectorAll(`[data-cs-id="${CSS.escape(id)}"]`)
    .forEach((el) => el.remove());
};

const CustomScripts = () => {
  useEffect(() => {
    let cancelled = false;
    const mountedIds = [];

    fetch(`${BASE}/api/scripts/public`, { credentials: "omit" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (cancelled || !Array.isArray(rows)) return;
        rows.forEach((s) => {
          if (!s || !s.enabled) return;
          mountScript(s);
          mountedIds.push(s.id);
        });
      })
      .catch(() => {
        /* network failure must not break the site */
      });

    return () => {
      cancelled = true;
      mountedIds.forEach(cleanupScript);
    };
  }, []);

  return null;
};

export default CustomScripts;
